import {
  Input,
  Directive,
  EventEmitter,
  Output,
  OnInit,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BaseComponent, EventBusService, WindowService } from '@ups/xplat/core';
import {
  dynamicAdminButtonControlNameActions,
  IApiModelSearchable,
  IDynamicModel,
  IDynamicModelProperties,
} from '../../models';
import { TranslateService } from '@ngx-translate/core';
import { filter, takeUntil } from 'rxjs/operators';
import {
  DynamicEventBusTypes,
  dynamicMarkTargetListener,
  dynamicEmitShowOnValuesTargets,
  dynamicShowOnValuesMatch,
  dynamicTextAutoFillClears,
  dynamicTextAutoFillKeeps,
  markOnConfigWrite,
} from '../../utils';
import { DynamicRenderService } from '../../services';
import { filterApiModel, mapApiModel } from './dynamic-item-model-mapper';

/* eslint-disable */
/**
 * Base level abstraction for all form element components.
 */
@Directive()
export abstract class DynamicItemBaseComponent
  extends BaseComponent
  implements OnInit
{
  win = inject(WindowService);
  translate = inject(TranslateService);
  eventBus = inject(EventBusService);
  formBuilder = inject(UntypedFormBuilder);
  // Shared for the whole leaf layer: template-observed state (config.options, isLoading,
  // etc.) is mutated in place from RxJS subscriptions, which never dirties an OnPush view.
  // markForCheck() at each mutation site readies the leaves for OnPush (WI 31903). No-op
  // under zone.js Default CD, so this is behavior-preserving. Protected so DynamicTypeaheadBaseComponent inherits it.
  // optional: a leaf built outside a view (spec harnesses, programmatic construction)
  // has no ChangeDetectorRef, and a required inject would throw NG0201.
  protected cdr = inject(ChangeDetectorRef, { optional: true });

  @Input()
  config: IDynamicModelProperties;
  @Input()
  group: UntypedFormGroup;
  @Input()
  formGroupName: string;
  @Input()
  dynamicService: DynamicRenderService;
  @Output()
  itemCreated: EventEmitter<unknown> = new EventEmitter();
  @Input()
  disableItem = false;

  valueObjectId: string | number | Date;
  isRedacted = false;

  /** we have a custom form validator dynamicValidatorNotLoading that makes the form invalid
   * if the isLoading_dynamic is true to prevent form from saving while a dropdown is loading */
  get isLoading(): boolean {
    const control = this.group.controls[this.config.formControlName];
    if (control) {
      return control['isLoading_dynamic'] ?? false;
    }

    return false;
  }

  /** we have a custom form validator dynamicValidatorNotLoading that makes the form invalid
   * if the isLoading_dynamic is true to prevent form from saving while a dropdown is loading */
  set isLoading(value: boolean) {
    const control = this.group.controls[this.config.formControlName];
    if (control) {
      const originalValue = control['isLoading_dynamic'];
      if (originalValue !== value) {
        control['isLoading_dynamic'] = value;
        control.updateValueAndValidity();
        this.cdr?.markForCheck();
      }
    }
  }

  /** Mirror of the isLoading flag for conflict resolution. When a control opts into
   * `conflictResolution.required`, an unresolved conflict invalidates the form via
   * dynamicValidatorNoConflict until the user picks keep/switch. (When not required, the
   * validator is never attached so this is a harmless no-op effect on validity.) */
  set conflictPending(value: boolean) {
    const control = this.group?.controls[this.config.formControlName];
    if (control) {
      const originalValue = control['hasUnresolvedConflict_dynamic'];
      if (originalValue !== value) {
        control['hasUnresolvedConflict_dynamic'] = value;
        control.updateValueAndValidity();
        this.cdr?.markForCheck();
      }
    }
  }

  /** True when the last remote typeahead search threw. Used with
   * dynamicValidatorNoSearchError when `options.blockSubmitOnSearchFailure` is true.
   * When that option is off (default), the flag still updates so callers can observe
   * `searchFailed` / the flag without blocking submit. */
  get searchError(): boolean {
    const control = this.group?.controls[this.config.formControlName];
    if (control) {
      return control['hasSearchError_dynamic'] ?? false;
    }
    return false;
  }

  set searchError(value: boolean) {
    const control = this.group?.controls[this.config.formControlName];
    if (control) {
      const originalValue = control['hasSearchError_dynamic'];
      if (originalValue !== value) {
        control['hasSearchError_dynamic'] = value;
        control.updateValueAndValidity();
        this.cdr?.markForCheck();
      }
    }
  }

  /** target formControlNames this control self-registered as a conflict source for (cleaned up on destroy). */
  private _registeredConflictTargets: Array<string> = [];
  private _releaseTargetListener?: () => void;
  /** The last value another control auto-pushed into this one (WI 34965). */
  private _lastAutoFilledValue: unknown;

  /** Resets and other in-place config writers emit after writing; ngDoCheck never runs on a
   * vcRef-created OnPush leaf, so mark here (Bug 35607). ngOnInit overrides w/o super call it. */
  protected markOnInPlaceConfigWrites() {
    markOnConfigWrite(this.eventBus, () => this.group, this.cdr, this.destroy$);
  }

  ngOnInit() {
    this.markOnInPlaceConfigWrites();
    if (this.dynamicService && !this.isRedacted) {
      if (this.config.formControlName) {
        // some controls just contain groups of other controls using 'formArrayName' below
        this._registerControl(this.config, this.group);
      } else if (this.config.formArrayName) {
        // category/nested/grouped controls
        const formArrayGroup = this.formBuilder.group({});
        const formArray: UntypedFormArray = this.formBuilder.array([
          formArrayGroup,
        ]);
        this.group.addControl(this.config.formArrayName, formArray);
      }
      if (this.config.disabled) {
        this.disableItem = true;
      }
    }
  }

  private _registerControl(config: IDynamicModel, group: UntypedFormGroup) {
    if (!this.dynamicService) {
      throw new Error(
        "_registerControl can't be called without DynamicRenderService"
      );
    }

    const settings = this.dynamicService.registerControlForGroup(config, group);
    if (settings.valueObjectId) {
      this.valueObjectId = <string>settings.valueObjectId;
    }

    if (
      config.options?.showOnValues &&
      group.controls[config.formControlName]
    ) {
      group.controls[config.formControlName].valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (this.dynamicService!.freshControlState.isResetting) {
            // clearActiveForm already restored the fresh-open hidden/enabled state
            return;
          }
          // WI 34928: a multi-select value matches when any picked option is listed.
          this.config.options.valueMatchFound = dynamicShowOnValuesMatch(
            this.config.options?.showOnValues.values,
            value,
            this.config.valueProperty,
            !!this.config.options?.isMultiSelect
          );

          // Another control may also reveal a target: hide only when none of them does.
          dynamicEmitShowOnValuesTargets(
            this.dynamicService!.itemTargetUpdate$,
            group.root,
            config.formControlName,
            this.config.options?.showOnValues,
            !this.config.options.valueMatchFound
          );
          // valueMatchFound on this control was mutated in place; dirty this view for OnPush.
          this.cdr?.markForCheck();
        });
    }

    this._releaseTargetListener ??= dynamicMarkTargetListener(group, config);
    this.dynamicService.itemTargetUpdate$
      .pipe(
        filter(
          (updates) =>
            updates && updates.formControlName === this.config.formControlName
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((updates: IDynamicModel) => {
        // Some other fields is calculating a property for this field in config.options.calculatedProperty() so setting loading indicator for this field
        if (updates.type === 'target-loading') {
          this.isLoading = updates.value ? true : false;
          return;
        } else if (updates.type === 'target-clear') {
          // Task 35033: empty only an auto-filled value, and never take the enable branch below.
          this.isLoading = false;
          const current = group.get(this.config.formControlName ?? '')?.value;
          if (dynamicTextAutoFillClears(current, this._lastAutoFilledValue)) {
            this.updateValue('');
            this._lastAutoFilledValue = '';
          }
          this.cdr?.markForCheck();
          return;
        } else {
          // remove dynamicValidatorNotLoading's
          this.isLoading = false;
        }

        if (updates.options) {
          this.config.options = {
            ...(this.config.options || {}),
            ...updates.options,
          };
        }
        let clearValue = false;
        if (updates.disabled || (updates.options && updates.options.hidden)) {
          // when activeFormResponse is present, don't auto-clear values
          // otherwise, always clear values when disabling or hiding controls
          clearValue = this.dynamicService!.activeFormResponse ? false : true;
          group.get(this.config.formControlName).disable({
            emitEvent: true,
          });
        } else if (updates.value) {
          let targetOptions;
          if (updates.options) {
            if (updates.options.typeahead) {
              targetOptions = updates.options.typeahead;
            }
            if (
              updates.options.updateOnValueChange &&
              updates.options.updateOnValueChange.length
            ) {
              targetOptions = updates.options.updateOnValueChange[0];
            }
          }
          // when targeting different controls with various options, the value may be async fetched via options instead of directly setting here
          if (!targetOptions) {
            if (updates.type === 'date') {
              this.updateDateValue(updates.value);
            } else if (
              // WI 34965: an opted-in text control keeps a typed or saved value.
              !dynamicTextAutoFillKeeps(
                this.config,
                group.get(this.config.formControlName ?? '')?.value,
                this._lastAutoFilledValue
              )
            ) {
              this.updateValue(updates.value);
              this._lastAutoFilledValue = updates.value;
            }
          }
        } else {
          group.get(this.config.formControlName).enable({
            emitEvent: true,
          });
        }

        if (clearValue) {
          this.updateValue(null);
        }
        // config.options / isLoading were mutated in place above; dirty this view for OnPush.
        this.cdr?.markForCheck();
      });

    if (config.options?.updateOnValueChange?.length) {
      // Self-register as a conflict source for every control this one pushes into, so the
      // target's conflict can light up THIS control's label too. Reliable in Mode A
      // (where activeModelGroup is empty). Cleaned up on destroy below.
      for (const entry of config.options.updateOnValueChange) {
        if (entry.formControlName) {
          this.dynamicService.registerConflictSource(
            this.config.formControlName,
            entry.formControlName,
            this.config.label
          );
          this._registeredConflictTargets.push(entry.formControlName);
        }
      }

      const validationConstraints = config.options.updateOnValueChange.filter(
        (options) => {
          return !!options.validation;
        }
      );
      if (validationConstraints.length) {
        for (const constraint of validationConstraints) {
          if (group.controls[constraint.formControlName]) {
            group.controls[constraint.formControlName].valueChanges
              .pipe(
                filter((v) => !!v),
                takeUntil(this.destroy$)
              )
              .subscribe((value) => {
                let message = '';
                if (['maxDate', 'minDate'].includes(constraint.validation)) {
                  const controlValue =
                    group.controls[this.config.formControlName].value;
                  // only consider truthy values on both constraints
                  if (controlValue) {
                    const currentValue = new Date(controlValue);
                    const valueOfOtherControl = new Date(value);

                    if (currentValue) {
                      if (constraint.validation === 'maxDate') {
                        // always compare days only
                        const maxAllowedDate = new Date(currentValue);
                        maxAllowedDate.setDate(maxAllowedDate.getDate() + 1);
                        if (valueOfOtherControl > maxAllowedDate) {
                          message = `You cannot set this value greater than "${this.config.label}"`;
                          this.updateValue(null, constraint.formControlName);
                        }
                      } else {
                        if (valueOfOtherControl < currentValue) {
                          message = `You cannot set this value less than "${this.config.label}"`;
                          this.updateValue(null, constraint.formControlName);
                        }
                      }
                    }
                  }
                }
                if (message) {
                  this.win.alert({
                    title: 'Oops',
                    message,
                    okButtonText: 'Ok',
                  });
                }
              });
          }
        }
      }
    }
  }

  override ngOnDestroy() {
    // Deregister conflict-source mappings so a closed/destroyed form leaves nothing stale on
    // the singleton service (Mode A never calls resetActiveForm()).
    if (this._registeredConflictTargets.length && this.dynamicService) {
      for (const targetName of this._registeredConflictTargets) {
        this.dynamicService.deregisterConflictSource(
          this.config?.formControlName,
          targetName
        );
      }
      this._registeredConflictTargets = [];
    }
    this._releaseTargetListener?.();
    super.ngOnDestroy();
  }

  isDisabled() {
    return this.config?.disabled || this.disableItem;
  }

  setRequired(validators?: Array<ValidatorFn>, targetFieldName?: string) {
    if (this.config) {
      this.setValidators(validators || [Validators.required], targetFieldName);
    }
  }

  setValidators(validators: Array<ValidatorFn>, targetFieldName?: string) {
    if (this.config && this.group) {
      const field = this.group.get(
        targetFieldName || this.config.formControlName
      );
      if (field) {
        field.setValidators(validators);
        field.updateValueAndValidity();
      }
    }
  }

  clearValidators(hideField?: boolean, targetFieldName?: string) {
    if (this.config && this.group) {
      const field = this.group.get(
        targetFieldName || this.config.formControlName
      );
      if (field) {
        // clear validators
        field.setValidators(null);
      }
      // also reset it's value:
      this.updateValue(null, targetFieldName);
      if (hideField) {
        // no parent value, hide this field
        if (!this.config.options) {
          this.config.options = {};
        }
        this.config.options.hidden = true;
        this.cdr?.markForCheck();
      }
    }
  }

  updateDateValue(value: unknown, targetFieldName?: string) {
    // can be implemented by dynamic components for custom date field handling
    this.updateValue(value, targetFieldName);
  }

  updateValue(value: unknown, targetFieldName?: string) {
    if (this.config && this.group) {
      const valueMatch: { [key: string]: unknown } = {};
      const fieldName = targetFieldName || this.config.formControlName;
      if (fieldName) {
        valueMatch[fieldName] = value;
        this.group.patchValue(valueMatch);
      }
    }
  }

  // Updates this form value along with other dependent values in one patch call
  updateWithDependents(
    value: unknown,
    dependentKeyValues: { [key: string]: unknown }
  ) {
    if (this.config && this.group) {
      const valueMatch = {};
      valueMatch[this.config.formControlName] = value;
      for (const key in dependentKeyValues) {
        if (dependentKeyValues.hasOwnProperty(key)) {
          valueMatch[key] = dependentKeyValues[key];
        }
      }
      this.group.patchValue(valueMatch, {
        emitEvent: true,
      });
    }
  }

  /**
   * Useful to map async responses to specific ui bound properties
   * @param res response payload
   */
  apiModelMapper(res: Array<IApiModelSearchable>): Array<IApiModelSearchable> {
    return mapApiModel(res, this.config.options);
  }

  filterData(res: Array<IApiModelSearchable>, searchString?: string) {
    // still routes through this.apiModelMapper so subclass overrides keep working.
    return filterApiModel(this.apiModelMapper(res), searchString);
  }

  delete() {
    this.eventBus.emit(DynamicEventBusTypes.dynamicRemoveControl, this.config);
  }

  cancel() {
    this.dynamicService?.resetActiveForm();
  }

  submit(draft?: boolean) {
    // check for custom builder behavior first
    if (
      dynamicAdminButtonControlNameActions.includes(this.config.formControlName)
    ) {
      const action = `dynamic${this.config.formControlName}`;
      this.eventBus.emit(
        DynamicEventBusTypes[action as keyof typeof DynamicEventBusTypes]
      );
    } else if (this.config.options?.submit) {
      this.dynamicService?.submitForm(this.group, null, draft);
    }
  }
}
